/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import './boot.scss';
import './styles/shared.scss';

import { initializeIcons } from '@fluentui/react/lib/Icons';
import { setFocusVisibility } from '@fluentui/react/lib/Utilities';
import * as Teams from '@microsoft/teams-js';
import { Theme } from '@msteams/verticals-ui-styles/Theme';
import { initializeFileTypeIcons } from '@uifabric/file-type-icons';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { ScenarioName, StatusName, StepName } from './logger';
import { LogCategory } from './logger/LogCategories';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { CoreColors } from './styles/teams';
import { getHostType } from './utilities/AppUtils';
import { initializeTacRequestSettingsAsync } from './utilities/TacSettings/TacRequestSettings';
import { AppHostType, getAppHost, initHost } from './utilities/TeamsUtils';
import { initiateTemplateCategories } from './utilities/TemplateUtils';
import { initializeUserSettingsAsync } from './utilities/UserSettings/UserSettingsUtils';
import { getHTMLDir } from './utilities/Utils';
import { App } from './views/App';
import { ErrorBoundary } from './views/containers/ErrorBoundary';

initializeIcons();
initializeFileTypeIcons();

// Initializes the appropriate host depending on the current platform (Teams or Browser)
// We MUST call this function before trying to access the app host
const hostType = getHostType();
const logCategory = LogCategory.AppBoot;
initHost(hostType)
    .then((): void => {
        const host = getAppHost();
        const logger = host.getLogger();
        const perfData = window.performance.timing;

        logger.logTraceInfo(
            LogCategory.AppBoot,
            `Request response time with Navigation Timing API: ${perfData.responseEnd - perfData.requestStart}`
        );
        logger.logTraceInfo(
            LogCategory.AppBoot,
            `Page load time with Navigation Timing API: ${perfData.loadEventEnd - perfData.navigationStart}`
        );
        logger.markScenario(ScenarioName.AppBoot, StepName.AppBootInitialized, StatusName.NA);

        let mainBackgroundColor = 'transparent';
        let className = 'desktop-client';

        if (host.getTheme() === Theme.Dark || host.getTheme() === Theme.Contrast) {
            className = 'desktop-client-dark-contrast';
        }

        if (host.isAndroid()) {
            className = host.getTheme() === Theme.Dark ? 'mobile-android-dark' : 'mobile-android';
            mainBackgroundColor = host.getTheme() === Theme.Dark ? CoreColors.appBlack06 : CoreColors.appWhite;
        } else if (host.isIOS()) {
            className = host.getTheme() === Theme.Dark ? 'mobile-iOS-dark' : 'mobile-iOS';
            mainBackgroundColor = host.getTheme() === Theme.Dark ? CoreColors.appBlack04 : CoreColors.appWhite;
        }

        initiateTemplateCategories(host.getLocale(), host.getEcsConfig());
        initializeUserSettingsAsync();
        initializeTacRequestSettingsAsync();

        // Set FluentUI initial focus styling as visible, so initial focus will be visible when users
        // enter our app through keyboard navigation.
        setFocusVisibility(true);

        ReactDOM.render(
            <div className={className} style={{ backgroundColor: mainBackgroundColor }}>
                <ErrorBoundary>
                    <App />
                </ErrorBoundary>
            </div>,
            document.getElementById('root')
        );

        if (hostType === AppHostType.Teams && getAppHost().getEcsConfig().MicrosoftTeamApprovals.ServiceWorkerEnabled) {
            serviceWorkerRegistration.register(host.getLogger());
        } else {
            serviceWorkerRegistration.unregister(host.getLogger());
        }

        const locale = host.getLocaleName();
        const HTMLDir = getHTMLDir(locale);
        logger.logTraceDebug(logCategory, `Rendering App Host with locale: ${locale}, dir: ${HTMLDir}`);
        document.documentElement.setAttribute('lang', locale);
        document.documentElement.setAttribute('dir', HTMLDir);
    })
    .catch((error: any): void => {
        const logger = getAppHost()?.getLogger();
        const errorMessage = JSON.stringify(error);

        console.error(errorMessage);
        logger?.logTraceError(logCategory, errorMessage);

        logger?.markScenario(ScenarioName.AppBoot, StepName.AppBootFailedWithError, StatusName.NA);
        logger?.failScenario(ScenarioName.AppBoot, {
            data: {
                errorMessage,
            },
        });

        logger?.logTraceInfo(logCategory, 'Notifying App load failed to Teams Platform');

        if (hostType === AppHostType.Teams) {
            Teams.app.notifyFailure({
                reason: 'Other' as any,
                message: errorMessage,
            });
        }
    });
